import { useEffect, useRef, useState } from 'react';
import { store } from '@/ui/store';
import { useStore } from '@/ui/useStore';
import { rowColToA1 } from '@/model/address';

/**
 * Find. A bar rather than a dialog, because a dialog over a spreadsheet covers
 * the thing you are searching, and the whole point is to look at what it found.
 */
export function FindBar({ onClose }: { onClose: () => void }): JSX.Element {
  useStore();
  const [query, setQuery] = useState(store.search?.query ?? '');
  const [allSheets, setAllSheets] = useState(store.search?.allSheets ?? false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  // Re-run as you type, and when the scope changes.
  useEffect(() => {
    store.find(query, allSheets);
  }, [query, allSheets]);

  const search = store.search;
  const count = search?.matches.length ?? 0;
  const at = search && search.index >= 0 ? search.index + 1 : 0;
  const current = search?.matches[search.index];

  const close = (): void => {
    store.closeSearch();
    onClose();
  };

  return (
    <div className="gt-find">
      <input
        ref={inputRef}
        className="gt-input"
        placeholder="Find in values and formulas…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            store.goToMatch(e.shiftKey ? -1 : 1);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            close();
          }
        }}
      />
      <span className={`gt-find-count${query && count === 0 ? ' is-none' : ''}`}>
        {query === '' ? '' : count === 0 ? 'No matches' : `${at} of ${count}`}
      </span>
      {current ? (
        <span className="gt-dim" title={current.text}>
          {rowColToA1(current.row, current.col)}
        </span>
      ) : null}
      <button className="gt-btn" disabled={count === 0} onClick={() => store.goToMatch(-1)} title="Previous (Shift+Enter)">
        ↑
      </button>
      <button className="gt-btn" disabled={count === 0} onClick={() => store.goToMatch(1)} title="Next (Enter)">
        ↓
      </button>
      <label className="gt-check">
        <input type="checkbox" checked={allSheets} onChange={(e) => setAllSheets(e.target.checked)} />
        All sheets
      </label>
      <button className="gt-btn" onClick={close} title="Close (Escape)">×</button>
    </div>
  );
}
